import { ComponentType } from 'react';

import { DndContext, DragEndEvent, DragOverlay, DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { SortableContext } from '@dnd-kit/sortable';

import { useSortable, useSortableDefaults } from '@components/drag-and-drop/defaults';
import { Stack } from '@components/stack';
import { Spacing } from '@styles/theme';

export type SortableListProps<T, Item> = {
  onChange: (activeIndex: number, overIndex: number) => void;
  items: Item[];
  DraggableComponent: ComponentType<T & DraggableComponentProps<Item>>;
  idProperty: keyof Item;
  componentProps: T;
  space?: Spacing;
};

export type DraggableComponentProps<Item> = {
  item: Item;
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
};

export const SortableList = <T, Item>({
  onChange,
  items,
  DraggableComponent,
  idProperty,
  componentProps,
  space = 2,
}: SortableListProps<T, Item>) => {
  const {
    items: localItems,
    itemIds,
    activeItem,
    sensors,
    collisionDetection,
    measuring,
    strategy,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
  } = useSortableDefaults({
    items,
    getId: (item) => String(item[idProperty]),
  });

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeIndex = itemIds.findIndex((id) => id === active.id);
    const overIndex = itemIds.findIndex((id) => id === over?.id);
    handleDragEnd(event);
    if (over && active.id !== over.id && activeIndex > -1 && overIndex > -1) {
      onChange(activeIndex, overIndex);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
      onDragEnd={onDragEnd}
    >
      <DragOverlay>
        {activeItem && (
          <DraggableComponent
            {...({ ...componentProps, item: activeItem } as T & DraggableComponentProps<Item>)}
          />
        )}
      </DragOverlay>
      <SortableContext items={itemIds} strategy={strategy}>
        <Stack space={space}>
          {localItems.map((item) => {
            const itemId = String(item[idProperty]);
            return (
              <DraggableItem<T, typeof item>
                key={itemId}
                itemId={itemId}
                Component={DraggableComponent}
                item={item}
                componentProps={componentProps}
              />
            );
          })}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};

type DraggableItemProps<T, Item> = {
  itemId: string;
  Component: ComponentType<T & DraggableComponentProps<Item>>;
  item: Item;
  componentProps: T;
};

const DraggableItem = <T, Item>({
  itemId,
  Component,
  item,
  componentProps,
}: DraggableItemProps<T, Item>) => {
  const { attributes, listeners, setNodeRef, style } = useSortable({ id: itemId });

  return (
    <div ref={setNodeRef} style={style}>
      <Component
        {...({ ...componentProps, item, attributes, listeners } as T &
          DraggableComponentProps<Item>)}
      />
    </div>
  );
};
